import React, { useState, useMemo } from 'react';
import { Target, Search, Sparkles, Filter, X, Save, CheckCircle2, AlertCircle, Settings, BookmarkCheck, Clock, History, FileSpreadsheet, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import Select from 'react-select';
import { PromocaoList } from './components/PromocaoList';
import { SavedPromocoesList } from './components/SavedPromocoesList';
import { PromocaoModal } from './components/PromocaoModal';
import { HistoryGeneralModal } from './components/HistoryGeneralModal';
import { HistoryDiffModal } from './components/HistoryDiffModal';
import { ImportXlsxModal } from './components/ImportXlsxModal';
import { DownloadModeloModal } from './components/DownloadModeloModal';
import { useMultipatrocinadores } from './hooks/useMultipatrocinadores';
import { Promocao, PromocaoSelecionada } from './types/multipatrocinadores';
import { multipatrocinadorService } from './services/multipatrocinadores';
import { PageTitle } from '../../../../_metronic/layout/core';

export const MultipatronizadoresScreen = () => {
  const { 
    promocoes, 
    loading, 
    error, 
    pagination, 
    distributors, 
    types, 
    loadingFilters, 
    searchPromocoes
  } = useMultipatrocinadores();
  const [promocoesSelecionadas, setPromocoesSelecionadas] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState<any>('');
  const [selectedDistributors, setSelectedDistributors] = useState<Array<{value: string; label: string}>>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [modalPromocao, setModalPromocao] = useState<{
    isOpen: boolean;
    promocao: Promocao | null;
    promocaoSelecionada?: PromocaoSelecionada;
  }>({
    isOpen: false,
    promocao: null,
  });
  const [showSavedPromocoes, setShowSavedPromocoes] = useState(false);
  const [showHistoryGeneral, setShowHistoryGeneral] = useState(false);
  const [showHistoryDiff, setShowHistoryDiff] = useState<{ isOpen: boolean; idRegraPromocao: string }>({
    isOpen: false,
    idRegraPromocao: ''
  });
  const [showImportXlsx, setShowImportXlsx] = useState(false);
  const [showDownloadModelo, setShowDownloadModelo] = useState(false);

  // IDs das promoções selecionadas para otimizar busca
  const promocoesSelecionadasIds = useMemo(() => 
    promocoesSelecionadas.map((ps:any) => parseInt(ps.promocao.IDRegraPromocao)), 
    [promocoesSelecionadas]
  );

  const handleSelecionarPromocao = (promocao:any) => {
    // Verificar se já existe configuração local para esta promoção
    let promocaoExistente = promocoesSelecionadas.find((ps:any) => ps.promocao.IDRegraPromocao === promocao.IDRegraPromocao);
    
    // Se não existe configuração local, mas a promoção já foi editada (tem campos extras), criar configuração
    if (!promocaoExistente && ((promocao as any).novoBudget !== undefined || (promocao as any).tipoSegmentacao)) {
      const budgetAtual = parseFloat(promocao.OrcamentoPromocao || '0');
      promocaoExistente = {
        promocao,
        budgetPlataforma: (promocao as any).budgetPlataforma || budgetAtual,
        novoBudget: (promocao as any).novoBudget || budgetAtual,
        confirmarBudget: (promocao as any).confirmarBudget || false,
        segmentacao: (promocao as any).segmentacao || {
          tipo: 'IDRegra',
          configuracoes: []
        }
      };
    }
    
    // Abrir modal com a promoção
    setModalPromocao({
      isOpen: true,
      promocao,
      promocaoSelecionada: promocaoExistente,
    });
  };

  const handleSalvarConfiguracao = (configuracao: PromocaoSelecionada) => {
    // Não adiciona mais à lista de selecionadas - configuração é salva diretamente no modal
  };

  const handleRemoverSelecao = (promocaoId:any) => {
    // Função removida - não há mais lista de selecionadas
  };

  const handleLimparSelecoes = () => {
    // Função removida - não há mais lista de selecionadas
  };

  const handleCloseModal = () => {
    setModalPromocao({
      isOpen: false,
      promocao: null,
    });
  };

  const handleSaveSuccess = () => {
    // Refresh list after successful save
    const distributorIds = selectedDistributors.map(d => d.value).join(',');
    searchPromocoes(searchTerm, pagination.offset, 50, distributorIds, selectedType);
  };

  const handleSearch = (e:any) => {
    e.preventDefault();
    const distributorIds = selectedDistributors.map(d => d.value).join(',');
    searchPromocoes(searchTerm, 0, 50, distributorIds, selectedType);
  };

  const handlePaginaChange = (offset:any) => {
    const distributorIds = selectedDistributors.map(d => d.value).join(',');
    searchPromocoes(searchTerm, offset, 50, distributorIds, selectedType);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDistributors([]);
    setSelectedType('');
    searchPromocoes('', 0, 50);
  };

  const handleSaveConfigurations = async () => {
    // Função removida - salvamento acontece diretamente no modal
  };

  const hasActiveFilters = searchTerm || selectedDistributors.length > 0 || selectedType;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleSelectSavedPromocao = (promocao: any) => {
    setShowSavedPromocoes(false);
    handleSelecionarPromocao(promocao);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/20">
      <PageTitle>Multipatrocinadores</PageTitle>
      <div className="w-full px-4 sm:px-6 py-6 sm:py-8">
        {/* Busca */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mb-6 sm:mb-8 bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Search className="h-4 h-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">Buscar Promoções</h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  {pagination.total.toLocaleString()} {pagination.total === 1 ? 'promoção encontrada' : 'promoções encontradas'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSavedPromocoes(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition-all shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/30"
              >
                <BookmarkCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Promoções Editadas</span>
              </button>

              <button
                onClick={() => setShowDownloadModelo(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-600 hover:from-cyan-700 hover:to-sky-800 text-white rounded-lg font-semibold transition-all shadow-md shadow-cyan-500/25 hover:shadow-lg hover:shadow-cyan-500/30"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Baixar Modelo</span>
              </button>

              <button
                onClick={() => setShowImportXlsx(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-600 hover:from-emerald-700 hover:to-teal-800 text-white rounded-lg font-semibold transition-all shadow-md shadow-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/30"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span className="hidden sm:inline">Importar XLSX</span>
              </button>

              <button
                onClick={() => setShowHistoryGeneral(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-600 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg font-semibold transition-all shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">Histórico Geral</span>
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                >
                  <X className="h-3 w-3" />
                  <span className="hidden sm:inline">Limpar Filtros</span>
                </button>
              )}
            </div>
          </div>
          
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Linha de busca */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Digite o nome da promoção..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
            </div>
            
            {/* Linha de filtros */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-blue-600" />
                    <span>Distribuidores</span>
                  </div>
                </label>
                <Select
                  isMulti
                  value={selectedDistributors}
                  onChange={(selected) => setSelectedDistributors(selected as Array<{value: string; label: string}>)}
                  options={distributors.map(d => ({ value: d.id, label: d.name }))}
                  placeholder="Selecione distribuidores..."
                  isDisabled={loadingFilters}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => 'Nenhum distribuidor encontrado'}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.75rem',
                      borderWidth: '2px',
                      borderColor: state.isFocused ? '#3b82f6' : '#e2e8f0',
                      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1'
                      },
                      minHeight: '42px'
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#dbeafe',
                      borderRadius: '0.5rem'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#1e40af',
                      fontWeight: '500'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#1e40af',
                      ':hover': {
                        backgroundColor: '#bfdbfe',
                        color: '#1e3a8a'
                      }
                    })
                  }}
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-purple-600" />
                    <span>Agrupamento Promoção</span>
                  </div>
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={loadingFilters}
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 disabled:opacity-50"
                >
                  <option value="">Todos os tipos</option>
                  {types.map((type) => (
                    <option key={type.name} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Botão de busca */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  <span>Buscando...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Buscar</span>
                </>
              )}
            </motion.button>
          </form>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl"
            >
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </motion.div>
          )}
        </motion.div>

        {/* Lista de Promoções */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <PromocaoList
            promocoes={promocoes}
            promocoesSelecionadas={promocoesSelecionadasIds}
            loading={loading}
            pagination={pagination}
            onSelecionarPromocao={handleSelecionarPromocao}
            onPaginaChange={handlePaginaChange}
            onViewHistory={(idRegraPromocao) => setShowHistoryDiff({ isOpen: true, idRegraPromocao })}
          />
        </motion.div>

        {/* Modal de Promoção */}
        <PromocaoModal
          isOpen={modalPromocao.isOpen}
          onClose={handleCloseModal}
          onSaveSuccess={handleSaveSuccess}
          promocao={modalPromocao.promocao}
          promocaoSelecionada={modalPromocao.promocaoSelecionada}
          onSave={handleSalvarConfiguracao}
        />

        {/* Modal de Promoções Salvas */}
        <SavedPromocoesList
          isOpen={showSavedPromocoes}
          onClose={() => setShowSavedPromocoes(false)}
          onSelectPromocao={handleSelectSavedPromocao}
        />

        {/* Modal de Histórico Geral */}
        <HistoryGeneralModal
          isOpen={showHistoryGeneral}
          onClose={() => setShowHistoryGeneral(false)}
          onViewDetails={(idRegraPromocao) => {
            setShowHistoryGeneral(false);
            setShowHistoryDiff({ isOpen: true, idRegraPromocao });
          }}
        />

        {/* Modal de Histórico com Diff */}
        <HistoryDiffModal
          isOpen={showHistoryDiff.isOpen}
          onClose={() => setShowHistoryDiff({ isOpen: false, idRegraPromocao: '' })}
          idRegraPromocao={showHistoryDiff.idRegraPromocao}
        />

        {/* Modal de Importação XLSX */}
        <ImportXlsxModal
          isOpen={showImportXlsx}
          onClose={() => setShowImportXlsx(false)}
          onImportSuccess={handleSaveSuccess}
        />

        {/* Modal de Download do Modelo */}
        <DownloadModeloModal
          isOpen={showDownloadModelo}
          onClose={() => setShowDownloadModelo(false)}
        />
      </div>
    </div>
  );
};

export default MultipatronizadoresScreen;